import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { IoHeartOutline, IoHeart, IoEllipsisHorizontal, IoTrashOutline, IoCreateOutline, IoClose, IoSend, IoArrowUndoOutline } from 'react-icons/io5';
import { FaMicrophone } from 'react-icons/fa';
import Avatar from './Avatar';
import VoiceRecorder from './VoiceRecorder';
import VoicePlayer from './VoicePlayer';
import MentionInput from './MentionInput';
import { postApi } from '../services/postApi';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import './CommentSection.css';

// Helper function to render content with clickable @mentions
const renderContentWithMentions = (content) => {
  if (!content) return null;

  const parts = content.split(/(@[a-zA-Z0-9_.]+)/g);

  return parts.map((part, index) => {
    if (part.startsWith('@')) {
      const username = part.slice(1);
      return (
        <Link key={index} to={`/profile/${username}`} className="mention">
          {part}
        </Link>
      );
    }
    return part;
  });
};

// Comment component defined OUTSIDE CommentSection to maintain stable identity across re-renders
const Comment = ({ comment, id, isReply = false, onReply, onLoadComments, formatTime, t }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isLiked, setIsLiked] = useState(comment.user_has_liked);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);

  const handleLike = async () => {
    try {
      if (isLiked) {
        await postApi.unlikeComment(comment.id);
        setIsLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
      } else {
        await postApi.likeComment(comment.id);
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('comment.confirmDelete') || 'Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await postApi.deleteComment(comment.id);
      await onLoadComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert(t('comment.deleteError') || 'Failed to delete comment');
    }
  };

  const handleUpdate = async () => {
    if (!editContent.trim()) return;

    try {
      await postApi.updateComment(comment.id, editContent);
      await onLoadComments();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
      alert(t('comment.updateError') || 'Failed to update comment');
    }
  };

  return (
    <div id={id} className={`comment ${isReply ? 'reply' : ''}`}>
      <Link to={`/profile/${comment.user?.id}`} className="comment-avatar">
        <Avatar user={comment.user} size="small" />
      </Link>
      <div className="comment-content">
        <div className="comment-bubble">
          <div className="comment-header">
            <Link to={`/profile/${comment.user?.id}`} className="comment-author">
              {comment.user?.display_name}
            </Link>
            <button className="icon-button comment-menu-btn" onClick={() => setShowMenu(!showMenu)}>
              <IoEllipsisHorizontal />
            </button>
            {showMenu && (
              <div className="comment-menu-dropdown">
                <button onClick={() => { setIsEditing(true); setShowMenu(false); }}>
                  <IoCreateOutline /> {t('common.edit') || 'Edit'}
                </button>
                <button onClick={handleDelete} className="danger">
                  <IoTrashOutline /> {t('common.delete') || 'Delete'}
                </button>
              </div>
            )}
          </div>
          {isEditing ? (
            <div className="comment-edit-form">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="comment-edit-textarea"
                rows="2"
              />
              <div className="comment-edit-actions">
                <button className="btn btn-text" onClick={() => setIsEditing(false)}>
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button className="btn btn-primary" onClick={handleUpdate}>
                  {t('common.save') || 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {(!comment.voice_url || (comment.content && comment.content !== 'Voice message')) && (
                <p className="comment-text">{renderContentWithMentions(comment.content)}</p>
              )}
              {comment.voice_url && (
                <VoicePlayer voiceUrl={comment.voice_url} duration={comment.voice_duration || 0} compact={true} />
              )}
            </>
          )}
        </div>
        <div className="comment-actions">
          <button className={`comment-action-btn ${isLiked ? 'liked' : ''}`} onClick={handleLike}>
            {isLiked ? <IoHeart /> : <IoHeartOutline />}
            {likeCount > 0 && <span>{likeCount}</span>}
          </button>
          {!isReply && (
            <button
              className="comment-action-btn"
              onClick={() => onReply(comment)}
            >
              {t('comment.reply') || 'Reply'}
            </button>
          )}
          <span className="comment-time">{formatTime(comment.created_at)}</span>
        </div>
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => (
              <Comment key={reply.id} comment={reply} isReply={true} onReply={onReply} onLoadComments={onLoadComments} formatTime={formatTime} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CommentSection = forwardRef(({ postId, onCommentAdded, initialVisible = 3, initialComments, highlightCommentId }, ref) => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const locale = i18n.language === 'vi' ? vi : enUS;
  const textareaRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const hasTriedLoadRef = React.useRef(false);
  const highlightTimeoutRef = React.useRef(null);

  // Focus input when replyTo changes
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [replyTo]);

  // Scroll to and highlight comment when highlightCommentId changes
  useEffect(() => {
    if (!highlightCommentId) return;

    // Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    const highlightAndScroll = async () => {
      // First check if comment is already in the list
      const findComment = (comments, id) => {
        for (const c of comments) {
          if (c.id === id) return c;
          if (c.replies) {
            const found = findComment(c.replies, id);
            if (found) return found;
          }
        }
        return null;
      };

      let comment = findComment(comments, highlightCommentId);

      // If comment not found, fetch it from API
      if (!comment) {
        try {
          const response = await postApi.getComment(highlightCommentId);
          if (response.success && response.data) {
            comment = response.data;
            // Add the comment to the appropriate location in state
            if (comment.parent_comment_id) {
              // It's a reply - add to parent's replies
              setComments(prev => prev.map(c => {
                if (c.id === comment.parent_comment_id) {
                  return {
                    ...c,
                    replies: [comment, ...(c.replies || [])]
                  };
                }
                return c;
              }));
            } else {
              // It's a top-level comment - prepend to list
              setComments(prev => [comment, ...prev]);
            }
          }
        } catch (err) {
          console.error('Error fetching highlighted comment:', err);
        }
      }

      // Wait for render then scroll to highlight
      setTimeout(() => {
        const commentElement = document.getElementById(`comment-${highlightCommentId}`);
        if (commentElement) {
          commentElement.scrollIntoView({ behavior: 'auto', block: 'center' });
          commentElement.classList.add('highlighted-comment');
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            commentElement.classList.remove('highlighted-comment');
          }, 3000);
        }
      }, 100);
    };

    highlightAndScroll();

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, [highlightCommentId, comments]);

  const loadComments = useCallback(async (force = false) => {
    // Skip if we've already loaded from initialComments (unless force is true)
    if (hasTriedLoadRef.current && !force) {
      setIsLoading(false);
      return;
    }
    hasTriedLoadRef.current = true;

    try {
      const response = await postApi.getPostComments(postId);
      if (isMountedRef.current) {
        setComments(response.data || []);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [postId]);

  // Handle initialComments when provided (from batch) or load individually
  useEffect(() => {
    isMountedRef.current = true;
    hasTriedLoadRef.current = false;

    // If initialComments is provided (even as empty array), use it
    if (initialComments !== undefined) {
      hasTriedLoadRef.current = true;
      setComments(initialComments || []);
      setIsLoading(false);
    } else {
      // Small delay to allow batch comments to arrive first
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && !hasTriedLoadRef.current) {
          loadComments();
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [initialComments, loadComments]);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }));

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && !voiceBlob) return;

    setIsSubmitting(true);
    try {
      // Create comment with text (or placeholder if voice-only)
      const commentContent = newComment.trim() || 'Voice message';
      const response = await postApi.createComment(postId, {
        content: commentContent,
        parent_comment_id: replyTo?.id || null
      });

      const comment = response.data;

      // Upload voice if present
      if (voiceBlob) {
        await postApi.uploadCommentVoice(comment.id, voiceBlob);
      }

      setNewComment('');
      setVoiceBlob(null);
      if (voiceUrl) {
        URL.revokeObjectURL(voiceUrl);
        setVoiceUrl(null);
      }
      setReplyTo(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Prepend new comment to existing list instead of reloading all comments
      // The new comment should appear immediately at the top
      const newCommentWithUser = {
        ...comment,
        user: comment.user || user,
        replies: [],
        likes_count: 0,
        is_liked: false,
      };
      
      // If it's a reply, we need to find the parent and add to its replies
      if (replyTo) {
        setComments(prev => prev.map(c => {
          if (c.id === replyTo.id) {
            return {
              ...c,
              replies: [newCommentWithUser, ...(c.replies || [])]
            };
          }
          return c;
        }));
      } else {
        // Top-level comment - prepend to the list
        setComments(prev => [newCommentWithUser, ...prev]);
      }
      
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      alert(t('comment.error') || 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartVoiceRecording = () => {
    setIsRecordingVoice(true);
  };

  const handleRecordingComplete = (blob) => {
    const url = URL.createObjectURL(blob);
    setVoiceBlob(blob);
    setVoiceUrl(url);
    setIsRecordingVoice(false);
  };

  const handleCancelRecording = () => {
    setIsRecordingVoice(false);
    setVoiceBlob(null);
    if (voiceUrl) {
      URL.revokeObjectURL(voiceUrl);
      setVoiceUrl(null);
    }
  };

  const handleRemoveVoice = () => {
    if (voiceUrl) {
      URL.revokeObjectURL(voiceUrl);
    }
    setVoiceBlob(null);
    setVoiceUrl(null);
  };

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const handleCommentChange = (e) => {
    setNewComment(e.target.value);
    // Only call autoResizeTextarea if we have a real DOM element
    if (e.target && typeof e.target.getBoundingClientRect === 'function') {
      autoResizeTextarea(e.target);
    } else if (textareaRef?.current) {
      // Fallback to using the ref if target is a synthetic event
      autoResizeTextarea(textareaRef.current);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment(e);
    }
  };

  const formatTime = useCallback((timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale });
    } catch {
      return timestamp;
    }
  }, [locale]);

  if (isLoading) {
    return <div className="comments-loading">{t('common.loading') || 'Loading...'}</div>;
  }

  const displayedComments = showAll ? comments : comments.slice(0, initialVisible);
  const hasMoreComments = comments.length > initialVisible;

  return (
    <div className="comment-section">
      <div className="comments-list">
        {comments.length === 0 ? (
          <p className="no-comments">{t('comment.noComments') || 'No comments yet. Be the first to comment!'}</p>
        ) : (
          <>
            {displayedComments.map(comment => (
              <Comment key={comment.id} id={`comment-${comment.id}`} comment={comment} onReply={setReplyTo} onLoadComments={loadComments} formatTime={formatTime} t={t} />
            ))}
            {!showAll && hasMoreComments && (
              <button
                className="show-more-comments"
                onClick={() => setShowAll(true)}
              >
                {t('comment.showMore') || `Show ${comments.length - initialVisible} more comment${comments.length - initialVisible > 1 ? 's' : ''}`}
              </button>
            )}
          </>
        )}
      </div>

      <form className="comment-form" onSubmit={handleSubmitComment}>
        {replyTo && (
          <div className="reply-indicator">
            <div className="reply-indicator-text">
              <IoArrowUndoOutline />
              <span>{t('comment.replyingTo') || 'Replying to'} <strong>{replyTo.user?.display_name || replyTo.username || 'Unknown User'}</strong></span>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} className="cancel-reply">
              <IoClose />
            </button>
          </div>
        )}

        {isRecordingVoice && (
          <VoiceRecorder
            onRecordingComplete={handleRecordingComplete}
            onCancel={handleCancelRecording}
            maxDuration={30}
          />
        )}

        {voiceUrl && !isRecordingVoice && (
          <div className="comment-voice-preview">
            <VoicePlayer voiceUrl={voiceUrl} duration={0} compact />
            <button
              type="button"
              className="remove-voice"
              onClick={handleRemoveVoice}
              aria-label="Remove voice"
            >
              <IoClose />
            </button>
          </div>
        )}

        <div className="comment-input-wrapper">
          {user && <Avatar user={user} />}
          <MentionInput
            value={newComment}
            onChange={handleCommentChange}
            onKeyDown={handleKeyDown}
            placeholder={replyTo ? t('comment.replyPlaceholder') || 'Write a reply...' : t('comment.placeholder') || 'Write a comment...'}
            className="comment-input"
            rows={1}
            maxLength={1000}
            disabled={isSubmitting || isRecordingVoice}
            inputRef={textareaRef}
          />
          <button
            type="button"
            className="comment-voice-btn"
            onClick={handleStartVoiceRecording}
            disabled={isSubmitting || voiceBlob !== null || isRecordingVoice}
            title={t('comment.addVoice') || 'Add voice message'}
          >
            <FaMicrophone />
          </button>
          <button
            type="submit"
            className="comment-submit"
            disabled={(!newComment.trim() && !voiceBlob) || isSubmitting}
          >
            <IoSend />
          </button>
        </div>
      </form>
    </div>
  );
});

CommentSection.displayName = 'CommentSection';

export default CommentSection;
